// Work Assignments admin surface (Phase 2): Assignments list + create/assign,
// Approvals queue (deep-linked ?tab=approvals from notifications), Types
// configuration. KaziPeople global theme tokens.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Briefcase, Check, Loader2, MapPin, Plus, Settings2, X } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import { getApiErrorMessage } from '@/lib/apiError';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import {
  AssignmentType, LocationMode, WorkAssignment, approveAssignment, assignmentStatusCounts, cancelAssignment,
  getLocationPolicy, listAssignments, listTypes, pendingApprovals, rejectAssignment,
  setLocationPolicy, upsertType,
} from '@/services/workAssignmentService';
import { ListWorkbench, LoadMore, sumStatusCounts, totalStatusCount, useDebouncedValue } from '@/features/shared/components/ListWorkbench';
import {
  ApprovalBadge, ASSIGN_STATUS_FILTERS, AssignmentDrawer, ContextLine, CreateAssignmentDialog, PLAN_EDITABLE,
  SourceChip, StatusBadge, fmtDT, useEmployeeNames,
} from './shared';

const PAGE = 20;

type Tab = 'assignments' | 'approvals' | 'types';

export const WorkAssignmentsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'assignments';
  const setTab = (k: Tab) => setParams(k === 'assignments' ? {} : { tab: k }, { replace: true });
  const { isTenantOwner, isHrManager } = useRoleAccess();
  const canManage = isTenantOwner || isHrManager;
  // Picker scope mirrors the server's create rule: HR/owner assign tenant-wide,
  // a manager only within their reporting subtree, an employee only themselves.
  const { nameOf, employees } = useEmployeeNames(canManage ? 'all' : 'team');

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            {t('wa.page_title', { defaultValue: 'Work Assignments' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('wa.page_sub', { defaultValue: 'Client visits, field work, routes, travel and training — connected to presence, attendance and approvals.' })}
          </p>
        </div>
      </div>
      <div className="flex gap-1 border-b border-border">
        {(['assignments', 'approvals', ...(canManage ? ['types' as const] : [])] as Tab[]).map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm rounded-t-md ${tab === k
              ? 'bg-card text-charcoal border border-border border-b-transparent font-medium'
              : 'text-muted-foreground hover:text-foreground'}`}>
            {t(`wa.tab_${k}`, { defaultValue: { assignments: 'Assignments', approvals: 'Approvals', types: 'Types' }[k] })}
          </button>
        ))}
      </div>
      {tab === 'assignments' && <AssignmentsTab nameOf={nameOf} employees={employees} canManage={canManage} />}
      {tab === 'approvals' && <ApprovalsTab nameOf={nameOf} />}
      {tab === 'types' && canManage && <TypesTab />}
    </div>
  );
};

const AssignmentsTab: React.FC<{
  canManage?: boolean;
  nameOf: (id?: string | null) => string;
  employees: Array<{ id: string; name: string }>;
}> = ({ nameOf, employees, canManage }) => {
  const { t } = useTranslation('admin');
  const promptText = usePrompt();
  const [list, setList] = useState<WorkAssignment[]>([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const qDebounced = useDebouncedValue(q, 300);
  const [openCreate, setOpenCreate] = useState(false);
  const [drawer, setDrawer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  // The assignment being edited. Same dialog as create, prefilled.
  const [editing, setEditing] = useState<WorkAssignment | null>(null);
  // Same split as MyAssignmentsPage: the list and the status-chip counts are two
  // requests, and only the list was refetched after a change — so cancelling an
  // assignment removed the row while "Scheduled" kept counting it until reload.
  const [dataVersion, setDataVersion] = useState(0);
  const refresh = useCallback(() => setDataVersion((n) => n + 1), []);
  const baseParams = React.useMemo(
    () => ({ limit: PAGE, ...(status ? { status } : {}), ...(qDebounced ? { q: qDebounced } : {}) }),
    [status, qDebounced],
  );
  const load = useCallback(() => {
    setBusy(true);
    listAssignments({ ...baseParams, offset: 0 })
      .then((rows) => { setList(rows); setHasMore(rows.length >= PAGE); })
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load assignments.')))
      .finally(() => setBusy(false));
  }, [baseParams]);
  useEffect(load, [load, dataVersion]);
  // Status-chip counts — depend on search, not the active status.
  useEffect(() => {
    let cancelled = false;
    assignmentStatusCounts(qDebounced ? { q: qDebounced } : {})
      .then((c) => { if (!cancelled) setCounts(c); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [qDebounced, dataVersion]);

  const loadMore = () => {
    setLoadingMore(true);
    listAssignments({ ...baseParams, offset: list.length })
      .then((rows) => { setList((prev) => [...prev, ...rows]); setHasMore(rows.length >= PAGE); })
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load assignments.')))
      .finally(() => setLoadingMore(false));
  };

  return (
    <div className="space-y-3">
      <ListWorkbench
        search={q}
        onSearch={setQ}
        searchPlaceholder={t('wa.search_ph', { defaultValue: 'Search assignments…' }) as string}
        statuses={ASSIGN_STATUS_FILTERS.map((s) => ({ key: s.key, label: t(`wa.status_${s.key.replace(/,/g, '_')}`, { defaultValue: s.label }) as string, count: sumStatusCounts(counts, s.key) }))}
        activeStatus={status}
        onStatus={setStatus}
        allLabel={t('wa.all_statuses', { defaultValue: 'All statuses' }) as string}
        allCount={totalStatusCount(counts)}
        right={(
          <Button onClick={() => setOpenCreate(true)} className="shrink-0">
            <Plus className="w-4 h-4 mr-1" />{t('wa.new', { defaultValue: 'New assignment' })}
          </Button>
        )}
      />
      <Card><CardContent className="p-0">
        {busy && list.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : list.length === 0 ? (
          <div className="p-8 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              {t('wa.empty', { defaultValue: 'No work assignments yet. Create the first one — or configure your assignment types to match how your teams work.' })}
            </p>
            <Button variant="outline" onClick={() => setOpenCreate(true)}>
              <Plus className="w-4 h-4 mr-1" />{t('wa.create_first', { defaultValue: 'Create an assignment' })}
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 px-4">{t('wa.col_title', { defaultValue: 'Assignment' })}</th>
              <th className="py-2 px-4">{t('wa.col_employee', { defaultValue: 'Employee' })}</th>
              <th className="py-2 px-4">{t('wa.col_planned', { defaultValue: 'Planned' })}</th>
              <th className="py-2 px-4">{t('wa.col_status', { defaultValue: 'Status' })}</th>
              <th className="py-2 px-4">{t('wa.col_approval', { defaultValue: 'Approval' })}</th>
              <th className="py-2 px-4">{t('wa.col_source', { defaultValue: 'Source' })}</th>
            </tr></thead>
            <tbody>{list.map((a) => (
              <tr key={a.id} className="border-b border-border cursor-pointer hover:bg-muted/50"
                onClick={() => setDrawer(a.id)}>
                <td className="py-2 px-4">
                  <p className="text-foreground">{a.title}</p>
                  <ContextLine a={a} />
                </td>
                <td className="py-2 px-4 text-foreground">{nameOf(a.employeeId)}</td>
                <td className="py-2 px-4 text-muted-foreground tabular-nums">{fmtDT(a.plannedStartAt)}</td>
                <td className="py-2 px-4"><StatusBadge status={a.status} /></td>
                <td className="py-2 px-4"><ApprovalBadge status={a.approvalStatus} /></td>
                <td className="py-2 px-4"><SourceChip a={a} /></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </CardContent></Card>
      <LoadMore
        shown={list.length}
        hasMore={hasMore}
        loading={loadingMore}
        onMore={loadMore}
        moreLabel={t('common.load_more', { defaultValue: 'Load more' }) as string}
        countLabel={(s) => t('wa.showing', { defaultValue: `${s} shown`, count: s }) as string}
      />
      {/* onCreated is refresh, not load: creating an assignment changes the
          status-chip counts as well as the list, and load() refetches only the
          list — so a new assignment appeared in the rows while "Scheduled" kept
          its old number until a reload. Same bug already fixed for cancel. */}
      <CreateAssignmentDialog open={openCreate} onClose={() => setOpenCreate(false)}
        onCreated={refresh} employees={employees}
        // A non-manager with no reports can only ever create for themselves —
        // lock the dialog to self (same UX as My Assignments) instead of
        // showing a one-name picker.
        selfEmployeeId={!canManage && employees.length === 1 ? employees[0].id : undefined} />
      <CreateAssignmentDialog open={!!editing} editing={editing}
        onClose={() => setEditing(null)}
        onCreated={() => { setEditing(null); refresh(); }}
        employees={employees} />
      <AssignmentDrawer id={drawer} onClose={() => setDrawer(null)} nameOf={nameOf}
        actions={(a) => (
          <div className="flex flex-wrap gap-2">
          {/* Edit only before it starts — the same rule the server enforces
              (PlanEditable). Once someone has checked in, the plan is the record
              of what they were asked to do, and Cancel is the honest action. */}
          {PLAN_EDITABLE.includes(a.status) && (
            <Button variant="outline" onClick={() => { setEditing(a); setDrawer(null); }}>
              {t('wa.edit', { defaultValue: 'Edit' })}
            </Button>
          )}
          {drawer ? (
          <Button variant="outline" className="text-danger"
            onClick={async () => {
              const reason = await promptText({
                title: t('wa.cancel_assignment', { defaultValue: 'Cancel assignment' }) as string,
                label: t('wa.cancel_why', { defaultValue: 'Cancellation reason:' }) as string,
                confirmLabel: t('wa.cancel_assignment', { defaultValue: 'Cancel assignment' }) as string,
                danger: true,
                required: true,
              });
              if (!reason) return;
              try {
                await cancelAssignment(drawer, reason);
                toast.success(t('wa.cancelled_ok', { defaultValue: 'Assignment cancelled.' }));
                setDrawer(null); refresh();
              } catch (e) { toast.error(getApiErrorMessage(e, 'Could not cancel.')); }
            }}>
            {t('wa.cancel_assignment', { defaultValue: 'Cancel assignment' })}
          </Button>
          ) : null}
          </div>
        )} />
    </div>
  );
};

const ApprovalsTab: React.FC<{ nameOf: (id?: string | null) => string }> = ({ nameOf }) => {
  const { t } = useTranslation('admin');
  const promptText = usePrompt();
  const [list, setList] = useState<WorkAssignment[]>([]);
  const load = useCallback(() => {
    pendingApprovals().then(setList)
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load approvals.')));
  }, []);
  useEffect(load, [load]);

  const decide = async (a: WorkAssignment, approve: boolean) => {
    try {
      if (approve) await approveAssignment(a.id);
      else {
        const reason = await promptText({
          title: t('wa.reject', { defaultValue: 'Reject assignment' }) as string,
          label: t('wa.reject_why', { defaultValue: 'Rejection reason:' }) as string,
          confirmLabel: t('wa.reject', { defaultValue: 'Reject' }) as string,
          danger: true,
          required: true,
        });
        if (!reason || reason.length < 3) return;
        await rejectAssignment(a.id, reason);
      }
      toast.success(approve
        ? t('wa.approved_ok', { defaultValue: 'Approved.' })
        : t('wa.rejected_ok', { defaultValue: 'Rejected.' }));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not record the decision.'));
    }
  };

  return (
    <Card><CardContent className="p-0">
      {list.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground">{t('wa.no_pending', { defaultValue: 'Nothing awaiting approval.' })}</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-2 px-4">{t('wa.col_title', { defaultValue: 'Assignment' })}</th>
            <th className="py-2 px-4">{t('wa.col_employee', { defaultValue: 'Employee' })}</th>
            <th className="py-2 px-4">{t('wa.col_planned', { defaultValue: 'Planned' })}</th>
            <th className="py-2 px-4 text-right" />
          </tr></thead>
          <tbody>{list.map((a) => (
            <tr key={a.id} className="border-b border-border">
              <td className="py-2 px-4">
                <p className="text-foreground">{a.title}</p>
                <ContextLine a={a} />
              </td>
              <td className="py-2 px-4 text-foreground">{nameOf(a.employeeId)}</td>
              <td className="py-2 px-4 text-muted-foreground tabular-nums">{fmtDT(a.plannedStartAt)}</td>
              <td className="py-2 px-4 text-right space-x-2 whitespace-nowrap">
                <Button size="sm" onClick={() => decide(a, true)}>
                  <Check className="w-4 h-4 mr-1" />{t('wa.approve', { defaultValue: 'Approve' })}
                </Button>
                <Button size="sm" variant="outline" className="text-danger" onClick={() => decide(a, false)}>
                  <X className="w-4 h-4 mr-1" />{t('wa.reject', { defaultValue: 'Reject' })}
                </Button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </CardContent></Card>
  );
};

const TypesTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [types, setTypes] = useState<AssignmentType[]>([]);
  const [locMode, setLocMode] = useState<LocationMode>('optional');
  const load = useCallback(() => {
    listTypes().then(setTypes)
      .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load types.')));
    getLocationPolicy().then(setLocMode).catch(() => { /* default optional */ });
  }, []);
  useEffect(load, [load]);

  const changeLocMode = async (mode: LocationMode) => {
    const prev = locMode;
    setLocMode(mode); // optimistic
    try {
      await setLocationPolicy(mode);
      toast.success(t('wa.loc_policy_saved', { defaultValue: 'Location policy updated.' }));
    } catch (e) {
      setLocMode(prev);
      toast.error(getApiErrorMessage(e, 'Could not update location policy.'));
    }
  };

  const toggle = async (x: AssignmentType, field: keyof AssignmentType, value: unknown) => {
    try {
      await upsertType({ ...x, [field]: value });
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not update the type.'));
    }
  };

  return (
    <Card><CardContent className="p-0">
      <div className="p-4 border-b border-border flex items-center gap-2 text-sm text-muted-foreground">
        <Settings2 className="w-4 h-4" />
        {t('wa.types_hint', { defaultValue: 'Types define behavior: presence label, approval, check-in policy. Industry flavors (Route, Service Call, Campaign…) are rows here — not custom builds.' })}
      </div>
      {/* Tenant-wide location capture — the floor over each type's GPS setting. */}
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2 text-sm">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <span className="text-foreground font-medium">{t('wa.loc_policy', { defaultValue: 'Location capture' })}</span>
        <select className="rounded-md border border-border bg-background text-foreground text-sm px-2 py-1"
          value={locMode} onChange={(e) => changeLocMode(e.target.value as LocationMode)}>
          <option value="disabled">{t('wa.loc_disabled', { defaultValue: 'Disabled — never capture location' })}</option>
          <option value="optional">{t('wa.loc_optional', { defaultValue: 'Optional — capture when the type asks' })}</option>
          <option value="required">{t('wa.loc_required', { defaultValue: 'Required — block check-in without location' })}</option>
        </select>
        <span className="text-xs text-muted-foreground">
          {t('wa.loc_policy_hint', { defaultValue: 'Applies to mobile check-in/out. Raw coordinates stay visible only to you and HR.' })}
        </span>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-muted-foreground border-b border-border">
          <th className="py-2 px-4">{t('wa.col_type', { defaultValue: 'Type' })}</th>
          <th className="py-2 px-4">{t('wa.col_presence', { defaultValue: 'Presence' })}</th>
          <th className="py-2 px-4 text-center">{t('wa.col_requires_approval', { defaultValue: 'Approval' })}</th>
          <th className="py-2 px-4 text-center">{t('wa.col_self', { defaultValue: 'Self-create' })}</th>
          <th className="py-2 px-4">{t('wa.col_checkin', { defaultValue: 'Check-in' })}</th>
          <th className="py-2 px-4">{t('wa.col_location', { defaultValue: 'Location' })}</th>
          <th className="py-2 px-4">{t('wa.col_pay', { defaultValue: 'Pay' })}</th>
          <th className="py-2 px-4 text-center">{t('wa.col_active', { defaultValue: 'Active' })}</th>
        </tr></thead>
        <tbody>{types.map((x) => (
          <tr key={x.id} className="border-b border-border">
            <td className="py-2 px-4">
              <p className="text-foreground">{x.label}</p>
              <p className="text-xs text-muted-foreground">{x.key} · {x.category}</p>
            </td>
            <td className="py-2 px-4">
              <Badge variant="secondary" className="bg-accent text-accent-foreground">{x.presenceLabel}</Badge>
            </td>
            <td className="py-2 px-4 text-center">
              <Switch checked={x.requiresApproval} onCheckedChange={(v) => toggle(x, 'requiresApproval', v)} />
            </td>
            <td className="py-2 px-4 text-center">
              <Switch checked={x.allowsSelfCreate} onCheckedChange={(v) => toggle(x, 'allowsSelfCreate', v)} />
            </td>
            <td className="py-2 px-4">
              <select className="rounded-md border border-border bg-background text-foreground text-sm px-2 py-1"
                value={x.checkInPolicy} onChange={(e) => toggle(x, 'checkInPolicy', e.target.value)}>
                {['none', 'optional', 'required'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </td>
            <td className="py-2 px-4">
              {/* GPS capture (which events) + geofence (verify against site radius).
                  Coordinates are point-in-time only; the tenant policy above can
                  disable capture entirely or require it. */}
              <div className="flex items-center gap-1.5">
                <select className="rounded-md border border-border bg-background text-foreground text-sm px-2 py-1"
                  value={x.gpsPolicy} onChange={(e) => toggle(x, 'gpsPolicy', e.target.value)}>
                  <option value="off">{t('wa.gps_off', { defaultValue: 'no GPS' })}</option>
                  <option value="checkin_only">{t('wa.gps_in', { defaultValue: 'check-in' })}</option>
                  <option value="checkin_and_checkout">{t('wa.gps_both', { defaultValue: 'in & out' })}</option>
                </select>
                {x.gpsPolicy !== 'off' && (
                  <>
                    <select className="rounded-md border border-border bg-background text-foreground text-sm px-2 py-1"
                      value={x.geofencePolicy} onChange={(e) => toggle(x, 'geofencePolicy', e.target.value)}>
                      <option value="off">{t('wa.geo_off', { defaultValue: 'no geofence' })}</option>
                      <option value="annotate">{t('wa.geo_annotate', { defaultValue: 'flag only' })}</option>
                      <option value="enforce">{t('wa.geo_enforce', { defaultValue: 'block outside' })}</option>
                    </select>
                    {x.geofencePolicy !== 'off' && (
                      <input type="number" min={20} max={5000}
                        className="w-20 rounded-md border border-border bg-background text-foreground text-xs px-2 py-1"
                        title={t('wa.geo_radius_title', { defaultValue: 'Allowed radius from site (metres)' }) as string}
                        defaultValue={x.geofenceRadiusM || 200}
                        onBlur={(e) => {
                          const v = Math.max(20, Math.min(5000, parseInt(e.target.value, 10) || 200));
                          if (v !== x.geofenceRadiusM) toggle(x, 'geofenceRadiusM', v);
                        }} />
                    )}
                  </>
                )}
              </div>
            </td>
            <td className="py-2 px-4">
              {/* Payroll seam: completed+approved assignments of a payable type
                  emit time facts; the entity Hours Policy prices the bucket. */}
              <div className="flex items-center gap-1.5">
                <select className="rounded-md border border-border bg-background text-foreground text-sm px-2 py-1"
                  value={x.payable} onChange={(e) => toggle(x, 'payable', e.target.value)}>
                  <option value="none">{t('wa.pay_none', { defaultValue: 'not paid' })}</option>
                  <option value="worked_hours">{t('wa.pay_hours', { defaultValue: 'worked hours' })}</option>
                  <option value="per_day">{t('wa.pay_day', { defaultValue: 'per day' })}</option>
                </select>
                {x.payable !== 'none' && (
                  <input
                    className="w-28 rounded-md border border-border bg-background text-foreground text-xs px-2 py-1 uppercase"
                    placeholder={t('wa.pay_bucket_ph', { defaultValue: 'FIELD_DAY' }) as string}
                    defaultValue={x.payrollHourType || ''}
                    onBlur={(e) => {
                      const v = e.target.value.trim().toUpperCase();
                      if (v !== (x.payrollHourType || '')) toggle(x, 'payrollHourType', v || undefined);
                    }}
                  />
                )}
              </div>
            </td>
            <td className="py-2 px-4 text-center">
              <Switch checked={x.active} onCheckedChange={(v) => toggle(x, 'active', v)} />
            </td>
          </tr>
        ))}</tbody>
      </table>
    </CardContent></Card>
  );
};

export default WorkAssignmentsPage;
